import React, { useState } from "react";
import Text from "@streeteasy/design-system/deprecated/Text";
import Title from "@streeteasy/design-system/deprecated/Title";
import { AsYouType } from "libphonenumber-js";
import { arrayOf, bool, func, shape, string } from "prop-types";

import EmailInput from "src/shared/components/Input/EmailInput";
import InputWithLabel from "src/shared/components/Input/InputWithLabel";
import DrawerFormButtons from "src/shared/contact-boxes/components/DrawerFormButtons";
import useAbTests from "src/shared/utilities/useAbTests";

import { validationSchema } from "../../utilities/validationSchema";
import ContactsList from "../ContactsList";
import GenericErrorMessage from "../GenericErrorMessage";
import { hasGenericErrors, inputErrorFor } from "./ContactFormErrors";
import * as S from "./styled";

const ContactForm = ({
  contacts,
  displayDetails,
  formErrors,
  formValues: { email, name, phone, searchPartnersEmails },
  onChange,
  onClose,
  onSendRequest,
  render,
  setFormErrors,
  setSubmitting,
  submitting
}) => {
  const {
    fossa_contact_box_email_input_update: abTestVariant,
    loading: abTestsLoading
  } = useAbTests("fossa_contact_box_email_input_update");

  const [visibleGenericError, updateGenericError] = useState(false);

  const fieldsMapping = {
    name: "sender_name",
    email: "sender_email"
  };

  const renderDisclaimerText = () =>
    `By pressing ${displayDetails.buttonCaption}, you agree that StreetEasy and real estate professionals may call/text you about your inquiry, which may involve use of automated means and prerecorded/artificial voices. You don't need to consent as a condition of buying any property, goods or services. Message/data rates may apply.`;

  const displayErrors = errors => {
    setFormErrors(errors);
    if (hasGenericErrors(errors)) {
      updateGenericError(true);
    }
  };

  const saveUserInfoInBrowser = () => {
    window.sessionStorage.setItem(
      "viewer",
      JSON.stringify({ name, email, phone })
    );
  };

  const onFormSubmit = async () => {
    setSubmitting(true);
    saveUserInfoInBrowser();

    try {
      const { data } = await onSendRequest();
      const resp = data.ask_question || data.request_tour;
      if (resp.errors) {
        displayErrors(resp.errors);
      }
    } catch (_) {
      updateGenericError(true);
    }
  };

  const inputError = inputName => inputErrorFor(formErrors, inputName);

  const handlePhoneChange = event => {
    const typedValue = new AsYouType("US").input(event.target.value);
    onChange(typedValue, event);
  };

  const validateInput = (event, inputName) => {
    if (validationSchema.fields[inputName].isValid(event.target.value)) {
      const errors = formErrors.filter(
        obj => obj.type !== fieldsMapping[inputName]
      );
      displayErrors(errors);
    }
  };

  const handleOnChange = (event, inputName = "") => {
    onChange(event.target.value, event);

    validateInput(event, inputName);

    if (formErrors.length === 0) {
      setSubmitting(false);
    }
  };

  const renderEmailField = () =>
    abTestVariant === "new" ? (
      <EmailInput
        error={inputError("email")}
        labelText="email"
        name="email"
        onChange={event => handleOnChange(event, "email")}
        required
        type="email"
        value={email}
      />
    ) : (
      <InputWithLabel
        errorMessage={inputError("email")}
        labelText="email"
        name="email"
        onChange={event => handleOnChange(event, "email")}
        required
        value={email}
      />
    );

  if (abTestsLoading) return null;

  return (
    <S.Form
      onSubmit={e => {
        e.preventDefault();
      }}
    >
      <S.FormContent>
        <S.TitleContainer>
          {displayDetails.title && (
            <Title type="primaryMd">{displayDetails.title}</Title>
          )}
          {displayDetails.subtitle && (
            <Text type="disclaimer">{displayDetails.subtitle}</Text>
          )}
        </S.TitleContainer>
        <InputWithLabel
          errorMessage={inputError("name")}
          labelText="name"
          name="name"
          onChange={event => handleOnChange(event, "name")}
          required
          value={name}
        />
        {renderEmailField()}
        <S.AddSearchPartner closedLabel="+ Add Search Partner" openLabel="">
          <InputWithLabel
            errorMessage={inputError("searchPartnersEmails")}
            hint="optional, separate with commas"
            labelText="search partner's email"
            name="searchPartnersEmails"
            onChange={handleOnChange}
            value={searchPartnersEmails}
          />
          <Text type="disclaimer">
            We&apos;ll email them about your request.
          </Text>
        </S.AddSearchPartner>
        <InputWithLabel
          errorMessage={inputError("phone")}
          hint="optional"
          labelText="phone number"
          name="phone"
          onChange={handlePhoneChange}
          value={phone}
        />
        {render(inputError)}
        <S.DisclaimerContainer>
          <Text type="disclaimer">{renderDisclaimerText()}</Text>
        </S.DisclaimerContainer>
        <S.TermsOfService>
          You also agree to our{" "}
          <a href="https://www.zillow.com/corp/Terms.htm">Terms of Use.</a>
        </S.TermsOfService>
        <ContactsList contacts={contacts} />
      </S.FormContent>
      <GenericErrorMessage
        closeGenericError={() => updateGenericError(false)}
        visibleGenericError={visibleGenericError}
      />
      <DrawerFormButtons
        isSubmitting={submitting}
        onCancel={onClose}
        onClickSubmit={onFormSubmit}
        submitText={displayDetails.buttonCaption}
      />
    </S.Form>
  );
};

ContactForm.propTypes = {
  formErrors: arrayOf(
    shape({
      type: string,
      messages: arrayOf(string)
    })
  ),
  setFormErrors: func,
  submitting: bool,
  setSubmitting: func,
  displayDetails: shape({
    title: string,
    subtitle: string,
    buttonCaption: string.isRequired
  }),
  onClose: func,
  onSendRequest: func,
  contacts: arrayOf(
    shape({
      name: string,
      image: shape({ url: string }),
      industry_professional: shape({ headshot: shape({ url: string }) })
    })
  ).isRequired,
  formValues: shape({
    name: string,
    email: string,
    phone: string
  }).isRequired,
  render: func.isRequired,
  onChange: func.isRequired
};

ContactForm.defaultProps = {
  displayDetails: {
    title: null,
    subtitle: null
  },
  onSendRequest: null,
  onClose: null,
  formErrors: [],
  setFormErrors: () => {},
  submitting: false,
  setSubmitting: () => {}
};

export default ContactForm;
